import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  FileSpreadsheet, 
  FileBarChart, 
  Download, 
  Settings, 
  Calendar,
  Building,
  Mail,
  Phone
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { 
  exportToCSV, 
  exportToExcel, 
  exportToPDF, 
  exportToWord, 
  prepareMemberDataForExport,
  type ExportConfig,
  type MemberExportData,
  type ExportStats
} from '@/lib/exportUtils';
import { format } from 'date-fns';

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: any[];
  stats?: ExportStats;
  title?: string;
}

const ExportDialog: React.FC<ExportDialogProps> = ({ 
  open, 
  onOpenChange, 
  data, 
  stats,
  title = 'Member Export'
}) => {
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel' | 'pdf' | 'word'>('excel');
  const [isExporting, setIsExporting] = useState(false);
  
  // Export configuration state
  const [config, setConfig] = useState<ExportConfig>({
    title: 'Member Directory',
    subtitle: 'Complete list of club members',
    filename: `members_export_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}`,
    includeStats: true,
    includeTimestamp: true,
    organizationName: 'OG Basketball Club',
    organizationAddress: 'Dar es Salaam, Tanzania',
    organizationPhone: '+255 XXX XXX XXX',
    organizationEmail: 'info@ogbasketball.com'
  });

  // Handle export configuration changes
  const updateConfig = (key: keyof ExportConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // Handle export execution
  const handleExport = async () => {
    if (data.length === 0) {
      toast({
        title: "No Data",
        description: "No member data available to export",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsExporting(true);
      
      // Prepare data for export
      const exportData = prepareMemberDataForExport(data);
      
      // Execute export based on selected format
      switch (exportFormat) {
        case 'csv':
          exportToCSV(exportData, config);
          break;
        case 'excel':
          exportToExcel(exportData, stats, config);
          break;
        case 'pdf':
          exportToPDF(exportData, stats, config);
          break;
        case 'word':
          await exportToWord(exportData, stats, config);
          break;
      }

      toast({
        title: "Export Successful",
        description: `Member data exported as ${exportFormat.toUpperCase()} file`,
        variant: "default"
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: `Failed to export data as ${exportFormat.toUpperCase()}`,
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Get format-specific features
  const getFormatFeatures = (format: string) => {
    switch (format) {
      case 'csv':
        return ['Basic data export', 'Lightweight file', 'Universal compatibility'];
      case 'excel':
        return ['Multiple worksheets', 'Statistics summary', 'Formatted tables', 'Column sizing'];
      case 'pdf':
        return ['Professional layout', 'Headers & footers', 'Statistics summary', 'Print-ready'];
      case 'word':
        return ['Rich formatting', 'Tables & statistics', 'Professional document', 'Editable format'];
      default:
        return [];
    }
  };

  // Get format icon
  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'csv':
        return <FileBarChart className="h-6 w-6" />;
      case 'excel':
        return <FileSpreadsheet className="h-6 w-6" />;
      case 'pdf':
        return <FileText className="h-6 w-6" />;
      case 'word':
        return <FileText className="h-6 w-6" />;
      default:
        return <FileText className="h-6 w-6" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Export member data in your preferred format with customizable options
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="format" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="format">Export Format</TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="format" className="space-y-6">
            {/* Export Format Selection */}
            <div>
              <Label className="text-base font-semibold mb-4 block">Choose Export Format</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { value: 'csv', label: 'CSV', description: 'Comma-separated values', color: 'bg-green-100 border-green-300' },
                  { value: 'excel', label: 'Excel', description: 'Microsoft Excel workbook', color: 'bg-blue-100 border-blue-300' },
                  { value: 'pdf', label: 'PDF', description: 'Portable document format', color: 'bg-red-100 border-red-300' },
                  { value: 'word', label: 'Word', description: 'Microsoft Word document', color: 'bg-purple-100 border-purple-300' }
                ].map((format) => (
                  <div
                    key={format.value}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      exportFormat === format.value
                        ? format.color + ' ring-2 ring-offset-2 ring-blue-500'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setExportFormat(format.value as any)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      {getFormatIcon(format.value)}
                      <Badge variant={exportFormat === format.value ? 'default' : 'secondary'}>
                        {format.label}
                      </Badge>
                    </div>
                    <h4 className="font-medium text-sm mb-1">{format.label} Export</h4>
                    <p className="text-xs text-gray-600 mb-3">{format.description}</p>
                    <div className="space-y-1">
                      {getFormatFeatures(format.value).map((feature, index) => (
                        <div key={index} className="flex items-center gap-1">
                          <div className="w-1 h-1 bg-current rounded-full" />
                          <span className="text-xs text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Export Preview
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Format:</span>
                  <p className="font-medium">{exportFormat.toUpperCase()}</p>
                </div>
                <div>
                  <span className="text-gray-600">Records:</span>
                  <p className="font-medium">{data.length} members</p>
                </div>
                <div>
                  <span className="text-gray-600">Filename:</span>
                  <p className="font-medium text-xs">{config.filename}.{exportFormat}</p>
                </div>
                <div>
                  <span className="text-gray-600">Size Est.:</span>
                  <p className="font-medium">
                    {exportFormat === 'pdf' ? '~500KB' : 
                     exportFormat === 'word' ? '~300KB' : 
                     exportFormat === 'excel' ? '~200KB' : '~50KB'}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            {/* Document Settings */}
            <div className="space-y-4">
              <h4 className="font-semibold text-base flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Document Settings
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Document Title</Label>
                  <Input
                    id="title"
                    value={config.title}
                    onChange={(e) => updateConfig('title', e.target.value)}
                    placeholder="Enter document title"
                  />
                </div>
                
                <div>
                  <Label htmlFor="filename">Filename</Label>
                  <Input
                    id="filename"
                    value={config.filename}
                    onChange={(e) => updateConfig('filename', e.target.value)}
                    placeholder="Enter filename"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="subtitle">Subtitle (Optional)</Label>
                <Input
                  id="subtitle"
                  value={config.subtitle}
                  onChange={(e) => updateConfig('subtitle', e.target.value)}
                  placeholder="Enter document subtitle"
                />
              </div>
            </div>

            {/* Organization Settings */}
            <div className="space-y-4">
              <h4 className="font-semibold text-base flex items-center gap-2">
                <Building className="h-4 w-4" />
                Organization Information
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="orgName">Organization Name</Label>
                  <Input
                    id="orgName"
                    value={config.organizationName}
                    onChange={(e) => updateConfig('organizationName', e.target.value)}
                    placeholder="Enter organization name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="orgPhone">Phone Number</Label>
                  <Input
                    id="orgPhone"
                    value={config.organizationPhone}
                    onChange={(e) => updateConfig('organizationPhone', e.target.value)}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="orgAddress">Address</Label>
                  <Textarea
                    id="orgAddress"
                    value={config.organizationAddress}
                    onChange={(e) => updateConfig('organizationAddress', e.target.value)}
                    placeholder="Enter organization address"
                    rows={2}
                  />
                </div>
                
                <div>
                  <Label htmlFor="orgEmail">Email</Label>
                  <Input
                    id="orgEmail"
                    value={config.organizationEmail}
                    onChange={(e) => updateConfig('organizationEmail', e.target.value)}
                    placeholder="Enter email address"
                  />
                </div>
              </div>
            </div>

            {/* Export Options */}
            <div className="space-y-4">
              <h4 className="font-semibold text-base flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Export Options
              </h4>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="includeStats"
                    checked={config.includeStats}
                    onCheckedChange={(checked) => updateConfig('includeStats', checked)}
                  />
                  <Label htmlFor="includeStats" className="text-sm font-normal">
                    Include member statistics summary
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="includeTimestamp"
                    checked={config.includeTimestamp}
                    onCheckedChange={(checked) => updateConfig('includeTimestamp', checked)}
                  />
                  <Label htmlFor="includeTimestamp" className="text-sm font-normal">
                    Include generation timestamp
                  </Label>
                </div>
              </div>
            </div>

            {/* Statistics Preview */}
            {stats && config.includeStats && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FileBarChart className="h-4 w-4" />
                  Statistics to Include
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-600">Total:</span>
                    <span className="font-medium ml-2">{stats.total}</span>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-600">Active:</span>
                    <span className="font-medium ml-2 text-green-600">{stats.active}</span>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-600">Pending:</span>
                    <span className="font-medium ml-2 text-yellow-600">{stats.pending}</span>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-600">Suspended:</span>
                    <span className="font-medium ml-2 text-orange-600">{stats.suspended}</span>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-600">Disabled:</span>
                    <span className="font-medium ml-2 text-red-600">{stats.disabled}</span>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-600">Expiring:</span>
                    <span className="font-medium ml-2 text-purple-600">{stats.expiring}</span>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isExporting}>
            Cancel
          </Button>
          
          <Button onClick={handleExport} disabled={isExporting} className="min-w-32">
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export {exportFormat.toUpperCase()}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDialog; 